import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Form, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { SXModalBase } from "./modals/SXModal";
import { useUnit } from "../features/unit/unitSlice";
import { useProject } from "../features/projects/projectSlice";
import { ng } from "@common/ngds";
import { CreateGUID } from "../helpers/funlib";
import { useAppSelector } from "../app/hooks";
import { selectAllMaterials, useMaterials } from "../features/materialsSections/materialSectionSlice";
import { NGDropdownListItem, SXDropdown } from "@common/components/ui-components/SXDropdown";
import { SXReinforcementMaterialDropdown } from "@common/components/ui-components/SXReinforcementMaterialDropdown";
import { CountryGroupableMaterial } from "@common/helpers/materialCountries";
import { MaterialListEntry, RestMaterial } from "@common/helpers/sharedTypes";
import { selectToken } from "../features/user/userSlice";
import { getSec } from "@common/helpers/protoGen";
import { useExactBarMarks } from "../features/projects/useExactBarMarks";

export interface SXInputReinforcementProps {
  // No props yet
}

const areAlmostEqual = (a: number, b: number, epsilon = 1.0e-9): boolean => Math.abs(a - b) <= epsilon;
const toMillimeters = (value: number): number => (Math.abs(Number(value)) > 1 ? Number(value) : Number(value) * 1.0e3);
const formatDiameterMm = (value: number): string => `${parseFloat(value.toFixed(3))}`;

export const SXInputReinforcement: React.FC<SXInputReinforcementProps> = (props: SXInputReinforcementProps) => {
  const { t } = useTranslation();
  const { getMaterials, getSections, getMaterial } = useMaterials();
  const token = useAppSelector(selectToken);
  const allMaterialLists = useAppSelector(selectAllMaterials) as Record<string, MaterialListEntry[] | undefined>;
  const [showExactBarCoords, setShowExactBarCoords] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoadingCodeValues, setIsLoadingCodeValues] = useState(false);
  const {
    project: selProject,
    setConcreteCover,
    setLongitudinalBarsY,
    setLongitudinalDiameterY,
    setLongitudinalBarsZ,
    setLongitudinalDiameterZ,
    setRadialBars,
    setRadialDiameter,
    setStirrupDiameter,
    setStirrupSpacing,
    setExactBarCoordinates,
    setLongitudinalMaterialGuid,
    setStirrupMaterialGuid,
    addStandaloneBar,
    updateStandaloneBar,
    deleteStandaloneBar,
    getCoverAndSpaces,
  } = useProject();
  const { sectionalUnit, toDisplaySectionalLength, fromDisplaySectionalLength } = useUnit();
  const exactBarMarks = useExactBarMarks();

  useEffect(() => {
    const reinforcementList = allMaterialLists?.Reinforcement;
    if (token && (!reinforcementList || reinforcementList.length === 0)) {
      getMaterials();
      getSections();
    }
  }, [allMaterialLists, getMaterials, getSections, token]);

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (showExactBarCoords && !e.target.checked) {
      // User is toggling OFF, show modal
      setShowConfirmModal(true);
    } else {
      setExactBarCoordinates(e.target.checked);
      setShowExactBarCoords(e.target.checked);
    }
  };

  const handleModalConfirm = (): void => {
    setExactBarCoordinates(false);
    setShowExactBarCoords(false);
    setShowConfirmModal(false);
  };

  const handleModalClose = (): void => {
    setShowConfirmModal(false);
  };

  // --------------------------------------------

  const inputJson: ng.input.Data_json | undefined = selProject?.data.data?.input;
  const structureJson: ng.structure.Data_json | null = inputJson?.structures[0] || null;
  const elementJson: ng.element.Data_json | null = structureJson?.elements?.[0] || null;
  const secDbJson: ng.sections.Section_json[] | null = inputJson?.secDb || null;

  const {
    section,
    cover,
    stirrupDiameter,
    stirrupSpacing,
    longitudinalBarsY,
    longitudinalDiameterY,
    longitudinalBarsZ,
    longitudinalDiameterZ,
    exactBarCoordinates,
    reinforcementMaterialGuid,
    longitudinalMaterialGuid,
    standaloneBars,
  } = useMemo(() => {
    const secDb: ng.sections.Section[] = Array.isArray(secDbJson)
      ? secDbJson.map((sec) => ng.sections.Section.fromObject(sec))
      : [];

    const element: ng.element.Data | null = elementJson ? ng.element.Data.fromObject(elementJson) : null;
    const beam: ng.beam.Data | null = element?.getBeam() || null;
    const rebars: ng.rebar.Data | null = beam?.getConcrete()?.rebars || null;
    const links: ng.link.Data | null = beam?.getConcrete()?.links || null;
    const distances: ng.design.concrete.CoverAndSpace[] | null = beam?.getConcrete()?.distances || null;
    const cover: number =
      distances?.find((dist) => dist?.side === ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM)?.cover || 0.0;
    const firstLinkGroup: ng.link.Group | null = links?.linkGrps?.[0] || null;
    const stirrupDiameter: number = firstLinkGroup?.diameter || 0.0;
    const stirrupSpacing: number = firstLinkGroup?.s || 0.0;
    const groups: ng.reinf.Group[] = rebars?.grps || [];
    const layers: ng.reinf.Layer[] = rebars?.lays || [];

    const yGroup: ng.reinf.Group | null = groups[0] || null;
    const zGroup: ng.reinf.Group | null = groups[1] || null;
    const topLayer: ng.reinf.Layer | null =
      layers.find((layer) => layer?.zone === ng.design.concrete.BeamSide.BEAM_SIDE_TOP) || null;
    const bottomLayer: ng.reinf.Layer | null =
      layers.find((layer) => layer?.zone === ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM) || null;
    const leftLayer: ng.reinf.Layer | null =
      layers.find((layer) => layer?.zone === ng.design.concrete.BeamSide.BEAM_SIDE_LEFT) || null;
    const rightLayer: ng.reinf.Layer | null =
      layers.find((layer) => layer?.zone === ng.design.concrete.BeamSide.BEAM_SIDE_RIGHT) || null;

    const longitudinalBarsY: number = topLayer?.getNum() ?? bottomLayer?.getNum() ?? 0;
    const longitudinalDiameterY: number = yGroup?.diameter ?? 0;
    const longitudinalBarsZ: number = leftLayer?.getNum() ?? rightLayer?.getNum() ?? 0;
    const longitudinalDiameterZ: number = zGroup?.diameter ?? 0;
    const exactBarCoordinates: boolean = Boolean(rebars?.exactBarCoordinates);
    const longitudinalMaterialGuid: string =
      groups.find((group) => typeof group?.mtrlGuid === "string" && group.mtrlGuid.length > 0)?.mtrlGuid ?? "";
    const stirrupMaterialGuid: string =
      links?.linkGrps?.find((group) => typeof group?.mtrlGuid === "string" && group.mtrlGuid.length > 0)?.mtrlGuid ??
      "";
    const reinforcementMaterialGuid: string =
      longitudinalMaterialGuid && stirrupMaterialGuid
        ? longitudinalMaterialGuid === stirrupMaterialGuid
          ? longitudinalMaterialGuid
          : ""
        : longitudinalMaterialGuid || stirrupMaterialGuid || "";
    const standaloneBars: ng.reinf.StandAloneBar[] = rebars?.standaloneBars ?? [];
    const secGuid: string | null = element?.getBeam()?.segments?.[0]?.layer?.start?.secGuid || null;
    const section: ng.sections.Section | null = secDb?.find((sec) => sec?.id?.guid === secGuid) || null;

    return {
      section,
      cover,
      stirrupDiameter,
      stirrupSpacing,
      longitudinalBarsY,
      longitudinalDiameterY,
      longitudinalBarsZ,
      longitudinalDiameterZ,
      exactBarCoordinates,
      reinforcementMaterialGuid,
      longitudinalMaterialGuid,
      standaloneBars,
    };
  }, [elementJson, secDbJson]);

  const reinforcementMaterials = useMemo((): CountryGroupableMaterial[] => {
    const fromAssetList = (allMaterialLists?.Reinforcement ?? [])
      .map((material) => ({
        guid: material.guid,
        name: material.name || material.guid,
        annex: material.annex,
        ownertype: material.ownertype,
      }))
      .filter((material) => typeof material.guid === "string" && material.guid.length > 0);

    if (fromAssetList.length > 0) {
      return fromAssetList;
    }

    // Fallback to referenced reinforcement materials already present in the project file.
    return (inputJson?.mtrlDb ?? [])
      .filter((material) => material?.type === "MTRL_REINFORCEMENT")
      .map((material) => ({ guid: material.id?.guid ?? "", name: material.id?.name ?? material.id?.guid ?? "" }))
      .filter((material) => material.guid.length > 0);
  }, [allMaterialLists, inputJson?.mtrlDb]);

  const selectedReinforcementMaterialFromProject = useMemo((): ng.material.Data_json | null => {
    const guid = reinforcementMaterialGuid || longitudinalMaterialGuid;
    if (!guid) {
      return null;
    }

    const material = inputJson?.mtrlDb?.find((mtrl) => mtrl?.id?.guid === guid) ?? null;
    if (!material?.reinforcementData) {
      return null;
    }

    return material;
  }, [inputJson?.mtrlDb, longitudinalMaterialGuid, reinforcementMaterialGuid]);

  const reinforcementDiameterOptions = useMemo((): NGDropdownListItem<number>[] => {
    const diameters = selectedReinforcementMaterialFromProject?.reinforcementData?.diameters ?? [];
    const uniqueMm = Array.from(
      new Set(
        diameters
          .map((item) => Number(item?.diameter))
          .filter((diameter) => Number.isFinite(diameter) && diameter > 0)
          .map((diameter) => Number(toMillimeters(diameter).toFixed(3))),
      ),
    ).sort((a, b) => a - b);

    return uniqueMm.map((diameterMm) => ({ id: diameterMm, name: formatDiameterMm(diameterMm) }));
  }, [selectedReinforcementMaterialFromProject]);

  const selectedDiameterY = useMemo((): NGDropdownListItem<number> | null => {
    return (
      reinforcementDiameterOptions.find((item) => areAlmostEqual(item.id, Number(longitudinalDiameterY), 1.0e-6)) ??
      null
    );
  }, [longitudinalDiameterY, reinforcementDiameterOptions]);

  const selectedDiameterZ = useMemo((): NGDropdownListItem<number> | null => {
    return (
      reinforcementDiameterOptions.find((item) => areAlmostEqual(item.id, Number(longitudinalDiameterZ), 1.0e-6)) ??
      null
    );
  }, [longitudinalDiameterZ, reinforcementDiameterOptions]);

  const selectedStirrupDiameter = useMemo((): NGDropdownListItem<number> | null => {
    const stirrupDiameterMm = toMillimeters(Number(stirrupDiameter ?? 0));
    return reinforcementDiameterOptions.find((item) => areAlmostEqual(item.id, stirrupDiameterMm, 1.0e-6)) ?? null;
  }, [reinforcementDiameterOptions, stirrupDiameter]);

  const radial: boolean = section?.type === ng.sections.SectionType.SECTION_TYPE_C;

  useEffect(() => {
    if (!selProject?.guid) {
      return;
    }

    setShowExactBarCoords(exactBarCoordinates);
  }, [exactBarCoordinates, selProject?.guid]);

  useEffect(() => {
    if (radial) {
      return;
    }

    if (!areAlmostEqual(Number(longitudinalDiameterY), Number(longitudinalDiameterZ), 1.0e-6)) {
      setLongitudinalDiameterZ(longitudinalDiameterY);
    }
  }, [radial, longitudinalDiameterY, longitudinalDiameterZ]);

  const buildCoverSpaceInput = (): ng.endpoint_args.rc_design.CoverSpaceInput | null => {
    if (!inputJson || !structureJson || !elementJson) {
      return null;
    }

    const element: ng.element.Data = ng.element.Data.fromObject(elementJson);
    const beam: ng.beam.Data | null = element.getBeam();
    const concrete: ng.beam.ConcreteElement | null | undefined = beam?.getConcrete();
    const rebars: ng.rebar.Data | null | undefined = concrete?.rebars;
    const links: ng.link.Data | null | undefined = concrete?.links;
    const groups: ng.reinf.Group[] = rebars?.grps ?? [];
    const mainDiameters: Set<number> = new Set<number>();
    for (const group of groups) {
      const diameter: number = Number(group.diameter);
      if (Number.isFinite(diameter) && diameter > 0) {
        mainDiameters.add(diameter > 1 ? diameter * 1.0e-3 : diameter);
      }
    }

    const stirrupDiameters: Set<number> = new Set<number>();
    for (const linkGroup of links?.linkGrps ?? []) {
      const diameter: number = Number(linkGroup.diameter);
      if (Number.isFinite(diameter) && diameter > 0) {
        stirrupDiameters.add(diameter > 1 ? diameter * 1.0e-3 : diameter);
      }
    }

    if (mainDiameters.size === 0 && stirrupDiameters.size === 0) {
      return null;
    }

    const secDb: ng.sections.Section[] = Array.isArray(secDbJson)
      ? secDbJson.map((sec) => ng.sections.Section.fromObject(sec))
      : [];
    const secGuid: string = beam?.segments?.[0]?.layer?.start?.secGuid ?? section?.id?.guid ?? "";
    const resolvedSection: ng.sections.Section | null =
      section ?? secDb.find((sec) => sec?.id?.guid === secGuid) ?? secDb[0] ?? null;

    if (!resolvedSection) {
      return null;
    }

    const sectionClone: ng.sections.Section = resolvedSection.deepClone();
    if (sectionClone.id?.guid !== secGuid) {
      sectionClone.id = ng.utils.ID.create({
        guid: secGuid || CreateGUID(),
        name: sectionClone.id?.name ?? resolvedSection.id?.name ?? "Temporary section",
      });
    }

    const concreteGuid: string = beam?.mtrlGuid ?? "";
    const rebarMaterialGuids: Set<string> = new Set(
      groups.map((group) => group.mtrlGuid).filter((guid): guid is string => Boolean(guid)),
    );
    const materialDb: ng.material.Data[] = (inputJson.mtrlDb ?? [])
      .map((material: ng.material.Data_json) => ng.material.Data.fromObject(material))
      .filter((material: ng.material.Data) => {
        const guid: string | undefined = material.id?.guid;
        return typeof guid === "string" && (guid === concreteGuid || rebarMaterialGuids.has(guid));
      });

    const startNode: ng.topology.ElementNode = ng.topology.ElementNode.create({
      id: ng.utils.ID.create({ guid: CreateGUID() }),
      coord: ng.geometry.Point3D.create({ x: 0, y: 0, z: 0 }),
    });

    const endNode: ng.topology.ElementNode = ng.topology.ElementNode.create({
      id: ng.utils.ID.create({ guid: CreateGUID() }),
      coord: ng.geometry.Point3D.create({ x: 0, y: 0, z: Number(beam?.segments?.[0]?.length) || 1 }),
    });

    const segment: ng.beam.Segment = ng.beam.Segment.create({
      id: ng.utils.ID.create({ guid: CreateGUID() }),
      length: Number(beam?.segments?.[0]?.length) || 1,
      layer: ng.beam.Layer.create({
        start: ng.beam.SecInPlane.create({ secGuid: sectionClone.id?.guid }),
        end: ng.beam.SecInPlane.create({ secGuid: sectionClone.id?.guid }),
      }),
    });

    const beamClone: ng.beam.Data = ng.beam.Data.create({
      concrete: concrete?.deepClone(),
      startNodeGuid: startNode.id?.guid,
      endNodeGuid: endNode.id?.guid,
      segments: [segment],
    });

    const rcDesignSettings: ng.design.concrete.ElementDesignSettings | null =
      element.designSettings?.rc?.deepClone() ?? null;
    const columnDesignSettings: ng.design.concrete.Column | null = rcDesignSettings?.getColumn() ?? null;
    if (columnDesignSettings) {
      if (columnDesignSettings.shearDesignType === ng.design.concrete.ShearDesignType.SHEAR_DESIGN_TYPE_UNSPECIFIED) {
        columnDesignSettings.shearDesignType =
          ng.design.concrete.ShearDesignType.SHEAR_DESIGN_TYPE_WITHOUT_SHEAR_REINFORCEMENT;
      }
    }

    const stageRcSpecific: ng.stage.RCSpecific_json | undefined =
      structureJson?.stages?.[0]?.elems?.[0]?.rc?.rcSpecifics?.[0];
    const stageLifeCategory: string | undefined = structureJson?.stages?.[0]?.elems?.[0]?.rc?.lifeCategory;
    const lcase: ng.loadcase.Data = ng.loadcase.Data.create({
      id: ng.utils.ID.create({ guid: CreateGUID() }),
      type: ng.loadcase.Type.TYPE_SELF_WEIGHT,
    });
    const lcomb: ng.loadcombination.Data = ng.loadcombination.Data.create({
      id: ng.utils.ID.create({ guid: CreateGUID() }),
      parts: [ng.loadcombination.CombinationPart.create({ lcaseGuid: lcase.id?.guid })],
    });
    const stageElement: ng.stage.Element = ng.stage.Element.create({
      guid: element.id?.guid ?? CreateGUID(),
      rc: ng.stage.RC.create({
        lifeCategory: stageLifeCategory != null ? ng.stage.LifeCategory_FromObject(stageLifeCategory) : undefined,
        rcSpecifics: [
          ng.stage.RCSpecific.create({
            exposureClass:
              stageRcSpecific?.exposureClass != null
                ? ng.stage.ExposureClass_FromObject(stageRcSpecific.exposureClass)
                : ng.stage.ExposureClass.EXPOSURE_CLASS_X0,
            waterCementRatioIndex: Number(stageRcSpecific?.waterCementRatioIndex ?? 0),
          }),
        ],
      }),
    });

    const structure: ng.structure.Data = ng.structure.Data.create({
      id: ng.utils.ID.create({ guid: CreateGUID() }),
      cc: structureJson.cc != null ? ng.structure.ConsequenceClass_FromObject(structureJson.cc) : undefined,
      reliabilityClass:
        structureJson.reliabilityClass != null
          ? ng.structure.ReliabilityClass_FromObject(structureJson.reliabilityClass)
          : undefined,
      nodes: [startNode, endNode],
      elements: [
        ng.element.Data.create({
          id: ng.utils.ID.create({ guid: element.id?.guid ?? CreateGUID() }),
          inspectionLevel: element.inspectionLevel,
          designSettings: ng.design.ElementDesignSettings.create({ rc: rcDesignSettings }),
          beam: beamClone,
        }),
      ],
      loading: ng.loading.Data.create({ combinations: [lcomb], loadcases: [lcase] }),
      stages: [
        ng.stage.Data.create({
          id: ng.utils.ID.create({ guid: CreateGUID() }),
          elems: [stageElement],
          lcombGuids: [lcomb.id?.guid ?? ""],
        }),
      ],
    });

    const tempInput: ng.input.Data = ng.input.Data.create({
      ec: ng.eurocode.DesignConfiguration.create({
        nationalAnnex:
          inputJson.ec?.nationalAnnex != null ? ng.eurocode.Annex_FromObject(inputJson.ec.nationalAnnex) : undefined,
        generation:
          inputJson.ec?.generation != null ? ng.eurocode.Generation_FromObject(inputJson.ec.generation) : undefined,
      }),
      id: ng.utils.ID.create({
        guid: CreateGUID(),
        name: "Temporary input for determining cover and spaces",
      }),
      secDb: [sectionClone],
      mtrlDb: materialDb,
      structures: [structure],
    });

    return ng.endpoint_args.rc_design.CoverSpaceInput.create({
      input: tempInput,
      barDiameters: Array.from(mainDiameters),
      stirrupDiameters: Array.from(stirrupDiameters),
    });
  };

  const loadReinforcementMaterial = async (guid: string): Promise<ng.material.Data | null> => {
    const material = (await getMaterial(guid)).payload as RestMaterial | undefined;
    return material?.kind === "data" ? ng.material.Data.fromObject(material.data.dbMtrl) : null;
  };

  const handleCodeValuesClick = async (): Promise<void> => {
    const request: ng.endpoint_args.rc_design.CoverSpaceInput | null = buildCoverSpaceInput();
    if (!request) {
      return;
    }

    const serialized: ng.endpoint_args.rc_design.CoverSpaceInput_json | null =
      ng.endpoint_args.rc_design.CoverSpaceInput.toObject(request);
    if (!serialized) {
      return;
    }

    setIsLoadingCodeValues(true);
    try {
      const response: unknown = await getCoverAndSpaces(serialized);
      const coverSpaceOutput: ng.endpoint_args.rc_design.CoverSpaceOutput | null = response
        ? ng.endpoint_args.rc_design.CoverSpaceOutput.fromObject(response)
        : null;

      if (!coverSpaceOutput) {
        return;
      }

      const diameters: number[] = [longitudinalDiameterY, longitudinalDiameterZ]
        .map((diameter) => Number(diameter))
        .filter((diameter) => Number.isFinite(diameter) && diameter > 0)
        .map((diameter) => (diameter > 1 ? diameter * 1.0e-3 : diameter));
      const maxDiameter: number = diameters.length > 0 ? Math.max(...diameters) : 0;
      const limits: ng.endpoint_args.rc_design.CoverSpaceOutput.RebarCoverSpace | undefined =
        coverSpaceOutput.minBarDistances.find((item) => item.diameter === maxDiameter);

      if (!limits) {
        return;
      }

      setConcreteCover(limits.cover * 1e3);
      if (limits.spaceBetweenLayer > 0) {
        setStirrupSpacing(limits.spaceBetweenLayer);
      }
    } catch (error) {
      console.error("Failed to fetch code values", error);
    } finally {
      setIsLoadingCodeValues(false);
    }
  };

  // --------------------------------------------

  return (
    <Container fluid id="sx-table-container" className="ps-0 px-0">
      <Row className="align-items-center">
        <Col>
          <Container style={{ fontSize: 12, color: "#696969" }}>
            <Row className="container-row" style={{ alignItems: "center" }}>
              <Form.Check
                type="switch"
                className="sx-switch"
                label={t("exact-bar-coords_.label")}
                id="sx-exact-bar-coords-switch"
                checked={showExactBarCoords}
                onChange={handleSwitchChange}
              />
            </Row>
          </Container>
        </Col>
      </Row>
      <Container style={{ color: "#696969" }}>
        <Row className="container-row" style={{ alignItems: "center" }}>
          {/* Cover and the code value button only mean something while the backend places the
              bars for us. With exact bar coordinates the user types each bar's Y/Z directly, so
              the cover is already contained in those numbers and there is nothing for the code
              values to fill in. The material stays: it applies to the bars either way. */}
          {!showExactBarCoords && (
            <Col className="pe-4">
              <Row>
                <Col className="editfield-label">
                  <span>
                    {t("concrete_cover_.label")} ({sectionalUnit})
                  </span>
                </Col>
                <Col xs={12} className="ps-0" style={{ height: 32 }}>
                  <SXEditField
                    fullWidth
                    id="sx-concrete-cover-input"
                    textAlign="left"
                    type="number"
                    fontSize={12}
                    //val={toDisplaySectionalLength(cover || 0)} // Should be like this, but cover is still in mm in NGDS.
                    val={cover || 0}
                    onUpdate={(value: number) => {
                      if (areAlmostEqual(Number(value), Number(cover || 0))) {
                        return;
                      }
                      setConcreteCover(value);
                    }}
                    disabled={false}
                  />
                </Col>
              </Row>
            </Col>
          )}
          {/* xs={6} rather than a bare Col so the dropdown keeps the same half-row width once the
              cover field next to it is gone - a plain .col would stretch it across the panel. */}
          <Col xs={6}>
            <Row>
              <Col className="editfield-label">
                <span>{t("material_.label")}</span>
              </Col>
              <Col xs={12} className="ps-0 px-0">
                <SXReinforcementMaterialDropdown
                  id="sx-reinforcement-material-select"
                  name="sx-reinforcement-material"
                  materials={reinforcementMaterials}
                  selectedGuid={reinforcementMaterialGuid}
                  loadMaterial={loadReinforcementMaterial}
                  onSelect={(guid) => {
                    void (async () => {
                      await setLongitudinalMaterialGuid(guid);
                      await setStirrupMaterialGuid(guid);
                    })();
                  }}
                />
              </Col>
            </Row>
          </Col>

          {!showExactBarCoords && (
            <Col xs={12} className="ps-0 pt-2 ms-0">
              <button
                className="action-btn-transparent ms-0"
                id="sx-reinf-code-value-btn"
                onClick={handleCodeValuesClick}
                disabled={isLoadingCodeValues}
              >
                {t("code_value_.label")}
              </button>
            </Col>
          )}
        </Row>
        <Row className="container-row" style={{ alignItems: "center" }}></Row>
      </Container>

      <Row>
        <Col xs={11} style={{ color: "#696969", marginBottom: 5 }}>
          <span className="header">{t("longitudinal_reinforcement_.label")}</span>
        </Col>
      </Row>
      {/* LONGITUDINAL REINFORCEMENT */}
      {!showExactBarCoords && (
        <Container fluid id="sx-table-container" key="longitudinal-reinforcement-container" className="ps-0 px-0">
          <Table hover size="sm" className="main-sx-table">
            <thead>
              <tr>
                <th className="text-left">{t("direction_.label")}</th>
                <th className="text-left">{t("num_of_bars_.label")}</th>
                <th className="text-left">
                  {t("diameter_.label")} ({sectionalUnit})
                </th>
              </tr>
            </thead>
            <tbody>
              {!radial && (
                <>
                  <tr id={"long-reinf-Y"} className="table-body align-items-center ju">
                    <td>
                      <SXEditField
                        type="text"
                        id={"direction_y"}
                        tableView
                        textAlign="left"
                        disabled={true}
                        displayUnit={false}
                        val={"Y"}
                      />
                    </td>
                    <td>
                      <SXEditField
                        type="number"
                        id={"num_of_bars_y"}
                        tableView
                        textAlign="left"
                        disableNegativeNumber={true}
                        min={2}
                        onUpdate={async (value: number) => {
                          const next = Math.max(2, Math.round(Number(value)));
                          const current = Math.max(2, Math.round(Number(longitudinalBarsY)));
                          if (next === current) {
                            return;
                          }
                          setLongitudinalBarsY(next);
                        }}
                        displayUnit={false}
                        val={longitudinalBarsY}
                      />
                    </td>
                    <td>
                      <SXDropdown
                        tableView
                        id="diameter_y"
                        name="diameter_y"
                        localisation={false}
                        list={reinforcementDiameterOptions}
                        val={selectedDiameterY}
                        lang={"en"}
                        notSelectedName={"select_.label"}
                        disabled={reinforcementDiameterOptions.length === 0}
                        onUpdate={async (value) => {
                          if (!value) {
                            return;
                          }
                          if (areAlmostEqual(Number(value.id), Number(longitudinalDiameterY))) {
                            return;
                          }
                          setLongitudinalDiameterY(value.id);
                          setLongitudinalDiameterZ(value.id);
                        }}
                      />
                    </td>
                  </tr>
                  <tr id={"long-reinf-Z"} className="table-body align-items-center">
                    <td>
                      <SXEditField
                        type="text"
                        id={"direction_z"}
                        tableView
                        textAlign="left"
                        disabled={true}
                        displayUnit={false}
                        val={"Z"}
                      />
                    </td>
                    <td>
                      <SXEditField
                        type="number"
                        id={"num_of_bars_z"}
                        tableView
                        textAlign="left"
                        disableNegativeNumber={true}
                        min={2}
                        onUpdate={async (value: number) => {
                          const next = Math.max(2, Math.round(Number(value)));
                          const current = Math.max(2, Math.round(Number(longitudinalBarsZ)));
                          if (next === current) {
                            return;
                          }
                          setLongitudinalBarsZ(next);
                        }}
                        displayUnit={false}
                        val={longitudinalBarsZ}
                      />
                    </td>
                    <td>
                      <SXDropdown
                        tableView
                        id="diameter_z"
                        name="diameter_z"
                        localisation={false}
                        list={reinforcementDiameterOptions}
                        val={selectedDiameterY}
                        lang={"en"}
                        notSelectedName={"select_.label"}
                        disabled={true}
                        onUpdate={() => {}}
                      />
                    </td>
                  </tr>
                </>
              )}

              {radial && (
                <tr id={"long-reinf-radial"} className="table-body align-items-center ju">
                  <td className="text-left" />
                  <td>
                    <SXEditField
                      type="number"
                      id={"radial-num-of-bars"}
                      tableView
                      textAlign="left"
                      disableNegativeNumber={true}
                      onUpdate={async (value: number) => {
                        const next = Math.max(0, Math.round(Number(value)));
                        const current = Math.max(0, Math.round(Number(longitudinalBarsY)));
                        if (next === current) {
                          return;
                        }
                        setRadialBars(value);
                      }}
                      displayUnit={false}
                      val={longitudinalBarsY}
                    />
                  </td>
                  <td>
                    <SXDropdown
                      tableView
                      id="radial-diameter"
                      name="radial-diameter"
                      localisation={false}
                      list={reinforcementDiameterOptions}
                      val={selectedDiameterY}
                      lang={"en"}
                      notSelectedName={"select_.label"}
                      disabled={reinforcementDiameterOptions.length === 0}
                      onUpdate={async (value) => {
                        if (!value) {
                          return;
                        }
                        if (areAlmostEqual(Number(value.id), Number(longitudinalDiameterY))) {
                          return;
                        }
                        setRadialDiameter(value.id);
                      }}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Container>
      )}

      {/* EXACT BAR COORDINATES (Triggered by the switch) */}
      {showExactBarCoords && (
        <Container fluid id="sx-table-container" className="ps-0 px-0 rebars">
          {/* this needs to be put in a .map once we have an API to fetch the number of bars and their coordinates */}
          <Row>
            <Col className="main-table-col">
              <Row className="table-body justify-content-center align-items-center p-0">
                <Col className="no-border">
                  <span style={{ color: "#696969" }}>
                    {" "}
                    <span className="header">{t("bar_coordinates_.label")}</span>
                  </span>
                </Col>
                <Col className="no-border" style={{ textAlign: "right" }}>
                  <button
                    className="action-btn-transparent mb-1 "
                    id="sx-reinf-add-rebar-btn"
                    onClick={() => {
                      addStandaloneBar({
                        y: 0,
                        z: 0,
                        diameter: longitudinalDiameterY || longitudinalDiameterZ || 0,
                        mtrlGuid: reinforcementMaterialGuid,
                      });
                    }}
                  >
                    {t("add_rebar_.label")}
                  </button>
                </Col>
              </Row>
              <Table hover size="sm" className="main-sx-table">
                <thead>
                  <tr>
                    <th className="text-center">#</th>
                    <th className="text-center">Y ({sectionalUnit})</th>
                    <th className="text-center">Z ({sectionalUnit})</th>
                    <th className="text-center">
                      {t("diameter_.label")} ({sectionalUnit})
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {standaloneBars.map((bar, index) => {
                    const guid = bar.id?.guid ?? `${index}`;
                    const y = Number(bar.startPoint?.y ?? 0);
                    const z = Number(bar.startPoint?.z ?? 0);
                    const diameter = Number(bar.diameter ?? 0);
                    // A native title rather than an overlay: a portalled overlay can flash a window
                    // scrollbar on its first frame (see "Portalled overlays" in .github/copilot-instructions.md).
                    const markReasons = exactBarMarks.get(guid);

                    return (
                      <tr
                        key={guid}
                        id={`standalone-bar-${guid}`}
                        className={`table-body align-items-center ju${markReasons ? " sx-exact-bar-flagged" : ""}`}
                        title={markReasons?.join("\n")}
                      >
                        <td className="text-center">
                          <SXEditField
                            type="number"
                            id={`coordinates_number_${guid}`}
                            tableView
                            disabled={true}
                            displayUnit={false}
                            val={index + 1}
                          />
                        </td>
                        <td className="text-center radiobutton-dropdown-box-height">
                          <SXEditField
                            type="number"
                            id={`coordinates_y_${guid}`}
                            tableView
                            disableNegativeNumber={false}
                            onUpdate={async (value: number) => {
                              const next = fromDisplaySectionalLength(value);
                              if (areAlmostEqual(next, y)) {
                                return;
                              }
                              updateStandaloneBar({ guid, y: next });
                            }}
                            displayUnit={false}
                            val={toDisplaySectionalLength(y)}
                          />
                        </td>
                        <td className="text-center radiobutton-dropdown-box-height" style={{ height: 30 }}>
                          <SXEditField
                            type="number"
                            id={`coordinates_z_${guid}`}
                            tableView
                            disableNegativeNumber={false}
                            onUpdate={async (value: number) => {
                              const next = fromDisplaySectionalLength(value);
                              if (areAlmostEqual(next, z)) {
                                return;
                              }
                              updateStandaloneBar({ guid, z: next });
                            }}
                            displayUnit={false}
                            val={toDisplaySectionalLength(z)}
                          />
                        </td>
                        <td className="text-center radiobutton-dropdown-box-height" style={{ height: 30 }}>
                          <SXDropdown
                            tableView
                            id={`coordinates_diameter_${guid}`}
                            name={`coordinates_diameter_${guid}`}
                            localisation={false}
                            list={reinforcementDiameterOptions}
                            val={
                              reinforcementDiameterOptions.find((item) => areAlmostEqual(item.id, diameter, 1.0e-6)) ??
                              null
                            }
                            lang={"en"}
                            notSelectedName={"select_.label"}
                            disabled={reinforcementDiameterOptions.length === 0}
                            onUpdate={async (value) => {
                              if (!value) {
                                return;
                              }
                              if (areAlmostEqual(Number(value.id), diameter)) {
                                return;
                              }
                              updateStandaloneBar({ guid, diameter: value.id });
                            }}
                          />
                        </td>
                        <td className="text-center">
                          <button
                            className="action-btn-transparent"
                            id={`delete-standalone-bar-${guid}`}
                            onClick={() => deleteStandaloneBar(guid)}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Container>
      )}

      {/** STIRRUPS */}
      <Container className="pb-2" style={{ color: "#696969" }}>
        <Row>
          <Col className="p-0">
            <span className="header">{t("stirrups_.label")}</span>
          </Col>
        </Row>
        <Row className="container-row" style={{ alignItems: "center" }}>
          <Col className="pe-4">
            <Row>
              <Col className="editfield-label">
                <span>
                  {t("diameter_.label")} ({sectionalUnit})
                </span>
              </Col>
              <Col xs={12} className="ps-0" style={{ height: 32 }}>
                <SXDropdown
                  id="sx-stirrups-diameter-input"
                  name="sx-stirrups-diameter"
                  localisation={false}
                  list={reinforcementDiameterOptions}
                  val={selectedStirrupDiameter}
                  lang={"en"}
                  notSelectedName={"select_.label"}
                  disabled={reinforcementDiameterOptions.length === 0}
                  onUpdate={async (value) => {
                    if (!value) {
                      return;
                    }
                    if (areAlmostEqual(Number(value.id), toMillimeters(Number(stirrupDiameter || 0)))) {
                      return;
                    }
                    setStirrupDiameter(value.id);
                  }}
                />
              </Col>
            </Row>
          </Col>

          <Col>
            <Row>
              <Col className="editfield-label">
                <span>
                  {t("spacing_.label")} ({sectionalUnit})
                </span>
              </Col>
              <Col xs={12} className="ps-0 px-0">
                <SXEditField
                  id="sx-stirrups-spacing-input"
                  fullWidth
                  textAlign="left"
                  type="number"
                  fontSize={12}
                  val={toDisplaySectionalLength(stirrupSpacing || 0)}
                  onUpdate={async (value: number) => {
                    const next = fromDisplaySectionalLength(value);
                    if (areAlmostEqual(Number(next), Number(stirrupSpacing || 0))) {
                      return;
                    }
                    setStirrupSpacing(next);
                  }}
                  disabled={false}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      <SXModalBase
        header={t("information_.label")}
        show={showConfirmModal}
        size="sm"
        close={handleModalClose}
        body={
          <Container>
            <Row>
              {t("exact-bar-coords_.modal_warning") ||
                "Going back to grid view will reset all manually inputted bar coordinates. Do you want to proceed?"}
            </Row>
          </Container>
        }
        footer={
          <div className="d-flex justify-content-end">
            <button className="btn btn-secondary me-2" onClick={handleModalClose}>
              {t("no_.label")}
            </button>
            <button className="action-btn btn btn-primary" onClick={handleModalConfirm}>
              {t("yes_.label")}
            </button>
          </div>
        }
      />
    </Container>
  );
};
